import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AppButton from "../../components/common/buttons/AppButton";
import CustomTypography from "../../components/common/form/CustomTypography";
import StorageCostDetailsListingTable from "../../components/storage-cost/StorageCostDetailsListingTable";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import ConfirmPopup from "../../components/common/pop-up/ConfirmPopup";
import AppSearchDropDown from "../../components/common/filter/AppSearchDropDown";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../models/SortType";
import StorageCostAPI from "../../../api/StorageCostAPI";
import ShipmentsAPI from "../../../api/ShipmentsAPI";
import {
  StorageCostDetailFilters,
  StorageCostDetailParams,
} from "../../../models/StorageCost/StrageCostDetailsFilters";

const getDefaultCostDateFrom = () =>
  dayjs().subtract(29, "day").format("YYYY-MM-DD");
const getDefaultCostDateTo = () => dayjs().format("YYYY-MM-DD");
const storageCostAPI = new StorageCostAPI();
const shipmentsAPI = new ShipmentsAPI();

const ensureSelectedOption = (
  options: FilterValue[],
  selectedValue: string | null,
): FilterValue[] => {
  if (!selectedValue) return options;
  const normalizedSelected = selectedValue.trim();
  if (!normalizedSelected) return options;
  const exists = options.some((option) => option.value === normalizedSelected);
  if (exists) return options;
  return [{ id: normalizedSelected, value: normalizedSelected }, ...options];
};

const ensureSelectedOptions = (
  options: FilterValue[],
  selectedValues: FilterValue[],
): FilterValue[] => {
  if (!selectedValues.length) return options;
  const merged = [...options];
  selectedValues.forEach((selected) => {
    if (!merged.some((option) => option.id === selected.id)) {
      merged.unshift({ id: selected.id, value: selected.value });
    }
  });
  return merged;
};

const parseDateQueryValue = (
  value: string | null,
  fallback: string,
): string => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : fallback;
};

const parseSelectedPackLabelMap = (
  value: string | null,
): Map<string, string> => {
  const labelMap = new Map<string, string>();
  if (!value) return labelMap;

  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((entry) => {
      const separatorIndex = entry.indexOf(":");
      if (separatorIndex <= 0) return;
      const id = entry.slice(0, separatorIndex).trim();
      const encodedLabel = entry.slice(separatorIndex + 1).trim();
      if (!id || !encodedLabel) return;
      try {
        labelMap.set(id, decodeURIComponent(encodedLabel));
      } catch {
        labelMap.set(id, encodedLabel);
      }
    });

  return labelMap;
};

const StorageCostDetailListing: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { userStore, storageCostStore } = useStores();
  const { id } = useParams<{ id: string }>();
  const searchParams = new URLSearchParams(location.search);
  const existingSelectedPacksLabelMap = parseSelectedPackLabelMap(
    searchParams.get("selected_packs_sku_values"),
  );
  const defaultCostDateFrom = getDefaultCostDateFrom();
  const defaultCostDateTo = getDefaultCostDateTo();
  const selectedPacksSkuParam = searchParams.get("selected_packs_sku") || "";
  const selectedPacksSkuLabelMap = parseSelectedPackLabelMap(
    searchParams.get("selected_packs_sku_values"),
  );
  const selectedPacksSku = selectedPacksSkuParam
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const initialFilters: StorageCostDetailParams = {
    costDateFrom: parseDateQueryValue(
      searchParams.get("cost_date_from"),
      defaultCostDateFrom,
    ),
    costDateTo: parseDateQueryValue(
      searchParams.get("cost_date_to"),
      defaultCostDateTo,
    ),
    selectedPacksSku,
    selectedPartnerTag: searchParams.get("selected_partner_tag") || null,
    sortBy: searchParams.get("sort_by") || "date",
    sortType:
      searchParams.get("sort_type") === SortType.ASC
        ? SortType.ASC
        : SortType.DESC,
  };
  const [isLoading, setIsLoading] = useState(true);
  const [draftFilters, setDraftFilters] = useState<StorageCostDetailFilters>({
    isFiltersExpanded: true,
    costDateFrom: dayjs(initialFilters.costDateFrom),
    costDateTo: dayjs(initialFilters.costDateTo),
    selectedPacksSku: initialFilters.selectedPacksSku.map((value: string) => ({
      id: value,
      value: selectedPacksSkuLabelMap.get(value) || value,
    })),
    selectedPartnerTag: initialFilters.selectedPartnerTag,
    partnerTagSearchText: "",
  });
  const [appliedFilters, setAppliedFilters] =
    useState<StorageCostDetailParams>(initialFilters);
  const [packsSkuOptions, setPacksSkuOptions] = useState<FilterValue[]>([]);
  const [partnerTagOptions, setPartnerTagOptions] = useState<FilterValue[]>([]);
  const [isRecalculateConfirmOpen, setIsRecalculateConfirmOpen] =
    useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedPacksSkuOption: FilterValue[] = draftFilters.selectedPacksSku;

  const selectedPartnerTagOption: FilterValue[] =
    draftFilters.selectedPartnerTag
      ? [
          {
            id: draftFilters.selectedPartnerTag,
            value: draftFilters.selectedPartnerTag,
          },
        ]
      : [];

  const packsSkuDropdownOptions = useMemo(
    () => ensureSelectedOptions(packsSkuOptions, draftFilters.selectedPacksSku),
    [draftFilters.selectedPacksSku, packsSkuOptions],
  );

  const partnerTagDropdownOptions = useMemo(
    () =>
      ensureSelectedOption(partnerTagOptions, draftFilters.selectedPartnerTag),
    [draftFilters.selectedPartnerTag, partnerTagOptions],
  );

  const fetchDetailFilterOptions = async (
    field: "packsSku" | "partnerTag",
    value: string,
  ) => {
    const trimmedSearch = value.trim();
    if (trimmedSearch.length < 3) return;
    try {
      if (field === "packsSku") {
        const response = await shipmentsAPI.searchPacks(trimmedSearch);
        const sourceRows = Array.isArray(response?.result)
          ? response.result
          : [];
        const optionsMap = new Map<string, FilterValue>();
        sourceRows.forEach((row: any) => {
          const optionId = String(row?.id || "").trim();
          const optionLabel = String(
            row?.value || row?.label || row?.name || row?.id || "",
          ).trim();
          if (!optionId || !optionLabel || optionsMap.has(optionId)) return;
          optionsMap.set(optionId, { id: optionId, value: optionLabel });
        });
        setPacksSkuOptions(Array.from(optionsMap.values()));
      } else {
        const response = await storageCostStore.searchTags(trimmedSearch);
        const sourceRows = Array.isArray(response?.result)
          ? response.result
          : Array.isArray((response as any)?.result?.list)
            ? (response as any).result.list
            : [];
        const optionsMap = new Map<string, FilterValue>();
        sourceRows.forEach((row: any) => {
          const optionValue = String(
            typeof row === "string"
              ? row
              : row?.value || row?.partner_tag || row?.label || row?.name || "",
          ).trim();
          const optionId = String(
            typeof row === "string"
              ? row
              : row?.id ||
                  row?.value ||
                  row?.partner_tag ||
                  row?.label ||
                  row?.name ||
                  "",
          ).trim();
          if (!optionId || !optionValue || optionsMap.has(optionId)) return;
          optionsMap.set(optionId, { id: optionId, value: optionValue });
        });
        const options = Array.from(optionsMap.values());
        setPartnerTagOptions(options);
      }
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Unable to fetch filter options. Please try again.",
      );
    }
  };

  const handlePacksSkuSearch = (value?: string) => {
    const searchValue = value || "";
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchValue.trim().length < 3) {
      setPacksSkuOptions([]);
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      fetchDetailFilterOptions("packsSku", searchValue);
    }, 1500);
  };

  const handlePartnerTagSearch = (value?: string) => {
    const searchValue = value || "";
    setDraftFilters((prev: StorageCostDetailFilters) => ({
      ...prev,
      partnerTagSearchText: searchValue,
    }));
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchValue.trim().length < 3) {
      setPartnerTagOptions([]);
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      fetchDetailFilterOptions("partnerTag", searchValue);
    }, 1500);
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    if (column === "id") return;
    setAppliedFilters((prev: StorageCostDetailParams) => ({
      ...prev,
      sortBy: column,
      sortType,
    }));
  };

  const getStorageCostDetail = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      await storageCostStore.getStorageCostDetail({
        id: Number(id),
        cost_date_from: appliedFilters.costDateFrom,
        cost_date_to: appliedFilters.costDateTo,
        sort_by: appliedFilters.sortBy || "date",
        sort_type: appliedFilters.sortType || SortType.DESC,
        selected_packs_sku:
          appliedFilters.selectedPacksSku.length > 0
            ? appliedFilters.selectedPacksSku.join(",")
            : undefined,
        partner_tag: appliedFilters.selectedPartnerTag || undefined,
      });
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Unable to fetch storage cost details. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRecalculateCost = async () => {
    if (!id) return;
    try {
      setIsRecalculating(true);
      const response = await storageCostStore.recalculateStorageCost({
        inventory_id: Number(id),
        cost_date_from: appliedFilters.costDateFrom,
        cost_date_to: appliedFilters.costDateTo,
      });
      setIsRecalculateConfirmOpen(false);
      showSuccess(
        response?.message || "Storage cost recalculated successfully.",
      );
      await getStorageCostDetail();
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Unable to recalculate storage cost. Please try again.",
      );
    } finally {
      setIsRecalculating(false);
    }
  };

  const downloadListingData = async () => {
    if (!id) return;
    try {
      const downloadParams = {
        id: Number(id),
        cost_date_from: appliedFilters.costDateFrom,
        cost_date_to: appliedFilters.costDateTo,
        sort_by: appliedFilters.sortBy || "date",
        sort_type: appliedFilters.sortType || SortType.DESC,
        selected_packs_sku:
          appliedFilters.selectedPacksSku.length > 0
            ? appliedFilters.selectedPacksSku.join(",")
            : undefined,
        partner_tag: appliedFilters.selectedPartnerTag || undefined,
      };
      const response =
        typeof storageCostStore.downloadStorageCostDetailReport === "function"
          ? await storageCostStore.downloadStorageCostDetailReport(
              downloadParams,
            )
          : await storageCostAPI.downloadStorageCostDetailReport(
              downloadParams,
            );

      if (response?.data) {
        const downloadUrl = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = response.filename
          ? response.filename
          : `Storage_Cost_Detail_Listing_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        showError("Error", "No data available to download.");
      }
    } catch (error: any) {
      showError(
        "Error",
        error?.message ||
          error?.response?.data?.message ||
          "Unable to download storage cost detail report. Please try again.",
      );
    }
  };

  const applyFilters = () => {
    setAppliedFilters((prev: StorageCostDetailParams) => ({
      ...prev,
      costDateFrom:
        draftFilters.costDateFrom?.format("YYYY-MM-DD") ||
        getDefaultCostDateFrom(),
      costDateTo:
        draftFilters.costDateTo?.format("YYYY-MM-DD") || getDefaultCostDateTo(),
      selectedPacksSku: draftFilters.selectedPacksSku.map(
        (item: FilterValue) => item.id,
      ),
      selectedPartnerTag: draftFilters.selectedPartnerTag,
    }));
  };

  const resetFilters = () => {
    const defaultCostDateFrom = getDefaultCostDateFrom();
    const defaultCostDateTo = getDefaultCostDateTo();
    setDraftFilters((prev: StorageCostDetailFilters) => ({
      ...prev,
      costDateFrom: dayjs(defaultCostDateFrom),
      costDateTo: dayjs(defaultCostDateTo),
      selectedPacksSku: [],
      selectedPartnerTag: null,
      partnerTagSearchText: "",
    }));
    setAppliedFilters((prev: StorageCostDetailParams) => ({
      ...prev,
      costDateFrom: defaultCostDateFrom,
      costDateTo: defaultCostDateTo,
      selectedPacksSku: [],
      selectedPartnerTag: null,
    }));
  };

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("cost_date_from", appliedFilters.costDateFrom);
    queryParams.set("cost_date_to", appliedFilters.costDateTo);
    queryParams.set("sort_by", appliedFilters.sortBy);
    queryParams.set("sort_type", appliedFilters.sortType);
    if (appliedFilters.selectedPacksSku.length > 0) {
      queryParams.set(
        "selected_packs_sku",
        appliedFilters.selectedPacksSku.join(","),
      );
      const selectedPackValues = appliedFilters.selectedPacksSku
        .map((packId) => {
          const selectedPack = draftFilters.selectedPacksSku.find(
            (item: FilterValue) => item.id === packId,
          );
          const resolvedPackLabel =
            selectedPack?.value && selectedPack.value !== packId
              ? selectedPack.value
              : existingSelectedPacksLabelMap.get(packId) ||
                selectedPack?.value;
          if (!resolvedPackLabel) return null;
          return `${packId}:${encodeURIComponent(resolvedPackLabel)}`;
        })
        .filter(Boolean)
        .join(",");
      if (selectedPackValues) {
        queryParams.set("selected_packs_sku_values", selectedPackValues);
      }
    }
    if (appliedFilters.selectedPartnerTag)
      queryParams.set(
        "selected_partner_tag",
        appliedFilters.selectedPartnerTag,
      );
    const nextSearch = `?${queryParams.toString()}`;
    if (nextSearch !== location.search) {
      navigate({ search: nextSearch }, { replace: true });
    }
  }, [
    navigate,
    location.search,
    appliedFilters,
    draftFilters.selectedPacksSku,
  ]);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    storageCostStore.clearSelectedDetail();
  }, [id, storageCostStore]);

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_STORAGE_COST]) || !id)
      return;

    getStorageCostDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userStore,
    id,
    appliedFilters.costDateFrom,
    appliedFilters.costDateTo,
    appliedFilters.selectedPartnerTag,
    appliedFilters.selectedPacksSku,
    appliedFilters.sortBy,
    appliedFilters.sortType,
  ]);

  useEffect(() => {
    const rows = storageCostStore.detailRows || [];
    if (!rows.length) return;

    const packLabelMap = new Map<string, string>();
    rows.forEach((row: any) => {
      const optionId = String(row?.id || "").trim();
      const optionLabel = String(row?.label || "").trim();
      if (!optionId || !optionLabel || packLabelMap.has(optionId)) return;
      packLabelMap.set(optionId, optionLabel);
    });

    setDraftFilters((prev: StorageCostDetailFilters) => {
      if (!prev.selectedPacksSku.length) return prev;

      let changed = false;
      const updatedSelectedPacks = prev.selectedPacksSku.map(
        (item: FilterValue) => {
          const mappedLabel = packLabelMap.get(String(item.id));
          if (!mappedLabel || mappedLabel === item.value) return item;
          changed = true;
          return {
            id: item.id,
            value: mappedLabel,
          };
        },
      );

      if (!changed) return prev;
      return {
        ...prev,
        selectedPacksSku: updatedSelectedPacks,
      };
    });
  }, [storageCostStore.detailRows]);

  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    [],
  );
  const pageInnerSx = {
    marginTop: "0.6rem",
    paddingX: 4,
    paddingY: 2,
    boxShadow:
      "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
    borderRadius: "4px",
    minHeight: { xs: "60vh", md: window.innerHeight - 110 },
  };

  if (!userStore.hasPermission([EmployeeRoles.VIEW_STORAGE_COST])) {
    return <PermissionBlockLabel />;
  }

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Paper>
        <Box sx={pageInnerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <CustomTypography variant="heading3">
                Storage Cost Details
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View detailed storage cost rows with pack, SKU, quantity, and
                partner tag filters.
              </CustomTypography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <AppButton
                variant="transparentPrimary"
                label="Download Data"
                onClick={downloadListingData}
              />
              {userStore.hasPermission([
                EmployeeRoles.RECALCULATE_INVENTORY_STORAGE,
              ]) && (
                <AppButton
                  variant="primary"
                  label="Recalculate Cost"
                  onClick={() => setIsRecalculateConfirmOpen(true)}
                  disabled={isLoading || isRecalculating}
                />
              )}
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{ mt: 2, borderRadius: 2, borderColor: "divider" }}
          >
            <Box
              onClick={() =>
                setDraftFilters((prev: StorageCostDetailFilters) => ({
                  ...prev,
                  isFiltersExpanded: !prev.isFiltersExpanded,
                }))
              }
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 0.5,
                cursor: "pointer",
              }}
            >
              <CustomTypography variant="heading6">Filters</CustomTypography>
              <IconButton size="small">
                {draftFilters.isFiltersExpanded ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </IconButton>
            </Box>
            <Collapse
              in={draftFilters.isFiltersExpanded}
              timeout="auto"
              unmountOnExit
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    width: "100%",
                    alignItems: "flex-end",
                  }}
                >
                  <Box sx={{ flex: "1 1 240px", minWidth: 220 }}>
                    <DateRangePickerNew
                      placeholder="Date Range"
                      startDate={draftFilters.costDateFrom}
                      endDate={draftFilters.costDateTo}
                      onRangeSelect={(range) => {
                        if (!range.startDate || !range.endDate) return;
                        setDraftFilters((prev: StorageCostDetailFilters) => ({
                          ...prev,
                          costDateFrom: range.startDate,
                          costDateTo: range.endDate,
                        }));
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 240px", minWidth: 220 }}>
                    <AppSearchDropDown
                      minWidth="180px"
                      isMultiSelect={true}
                      title="Search pack by name or client SKU"
                      value={selectedPacksSkuOption}
                      fontSize="14px"
                      list={packsSkuDropdownOptions}
                      local={false}
                      handleSearch={handlePacksSkuSearch}
                      handleSelect={(items: FilterValue[]) => {
                        setDraftFilters((prev: StorageCostDetailFilters) => ({
                          ...prev,
                          selectedPacksSku: items,
                        }));
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 240px", minWidth: 220 }}>
                    <AppSearchDropDown
                      textFieldValue={draftFilters.partnerTagSearchText}
                      isMultiSelect={false}
                      fontSize="14px"
                      title="Partner Tag"
                      value={selectedPartnerTagOption}
                      list={partnerTagDropdownOptions}
                      local={false}
                      handleSearch={handlePartnerTagSearch}
                      handleSelect={(items) => {
                        setDraftFilters((prev: StorageCostDetailFilters) => ({
                          ...prev,
                          partnerTagSearchText: "",
                          selectedPartnerTag:
                            items.length > 0 ? items[0].value : null,
                        }));
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: "0 0 140px", minWidth: 140 }}>
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={applyFilters}
                      customStyles={{ height: 40, width: "100%" }}
                    />
                  </Box>
                  <Box sx={{ flex: "0 0 140px", minWidth: 140 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear All"
                      onClick={resetFilters}
                      customStyles={{ height: 40, width: "100%" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          <Box mt={3}>
            <StorageCostDetailsListingTable
              sortBy={appliedFilters.sortBy}
              sortType={appliedFilters.sortType}
              onSortChange={handleSortChange}
              detailRows={storageCostStore.detailRows || []}
              isLoading={isLoading}
            />
          </Box>
          <ConfirmPopup
            open={isRecalculateConfirmOpen}
            title="Recalculate storage cost?"
            message={`This will recalculate inventory cost for inventory id #${id} between ${appliedFilters.costDateFrom} and ${appliedFilters.costDateTo}. Do you want to continue?`}
            confirmText="Recalculate"
            cancelText="Cancel"
            onCancel={() => setIsRecalculateConfirmOpen(false)}
            onConfirm={handleConfirmRecalculateCost}
            loading={isRecalculating}
          />
        </Box>
      </Paper>
    </Grid2>
  );
};

export default observer(StorageCostDetailListing);
